import { Theme, Typography, useMediaQuery } from '@mui/material';
import { FC, useState } from 'react';

import { Piece } from '@apiContract';
import { CameraCapture } from '@components/CameraCapture';
import Header from '@components/Capture/components/Header';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';
import { ValidationData } from '@components/MailpieceActions/types';

import { ActionComponentProps } from '../../../index';
import { ActionWithInfo } from '../ActionWithInfo';
import ImagePreviewContainer from './ImagePreviewContainer';
import { getButtonConfigs } from './helper';
import { useUploadImage } from './useUploadImage';

type CaptureMode = 'capture' | 'review';

interface ActionProps {
  mailpieces: Piece[];
  open: boolean;
  type?: string;
  mode: CaptureMode;
  image: string | null;
  isMobile: boolean;
  skipNotifyRecipient: boolean;
  comment: string;
  notifyMailroom: boolean;
  validationData?: ValidationData;
  onClose: () => void;
  onFinish: () => void;
  onCapture: (imageSrc: string) => void;
  onRetake: () => void;
}

const FinishDeliveryWithImageAction: FC<ActionProps> = ({
  mailpieces,
  open,
  type,
  mode,
  image,
  isMobile,
  skipNotifyRecipient,
  comment,
  notifyMailroom,
  validationData,
  onClose,
  onFinish,
  onCapture,
  onRetake,
}) => {
  const { uploadImage, loading } = useUploadImage(mailpieces, onFinish, type, validationData);

  const handleComplete = () => {
    if (image) {
      void uploadImage(image, skipNotifyRecipient, comment, notifyMailroom);
    }
  };

  const buttons = getButtonConfigs(onRetake, handleComplete, loading);

  return (
    <DialogDarkFullscreen open={open}>
      <Header
        onClose={onClose}
        title={mode === 'capture' ? 'Capture Image' : 'Review'}
        buttons={mode === 'capture' ? [] : buttons}
      />

      {mode === 'capture' && <CameraCapture capture={onCapture} disabled={loading} />}
      {mode === 'review' && image && <ImagePreviewContainer image={image} isMobile={isMobile} buttons={buttons} />}
    </DialogDarkFullscreen>
  );
};

const FinishDeliveryWithImage: FC<ActionComponentProps> = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  type,
  validationData,
}) => {
  const [mode, setMode] = useState<CaptureMode>('capture');
  const [image, setImage] = useState<string | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const handleImageCapture = (imageSrc: string) => {
    setImage(imageSrc);
    setMode('review');
  };

  const resetCapture = () => {
    setImage(null);
    setMode('capture');
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title="Complete with image"
      onClose={onActionFinish}
      validationData={validationData}
      acceptButtonTitle="Capture Image"
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {mailpieces.length === 1 ? (
            <>Please capture an image of this item.</>
          ) : (
            <>
              Please capture an image of <strong>{mailpieces.length}</strong> items.
            </>
          )}
        </Typography>
      }
    >
      {({ step, skipNotifyRecipient, comment, notifyMailroom, onClose, onFinish }) => (
        <FinishDeliveryWithImageAction
          mailpieces={mailpieces}
          open={actionStarted && step === 'action'}
          type={type}
          mode={mode}
          image={image}
          isMobile={isMobile}
          skipNotifyRecipient={skipNotifyRecipient}
          comment={comment}
          notifyMailroom={notifyMailroom}
          validationData={validationData}
          onClose={onClose}
          onFinish={() => {
            resetCapture();
            onFinish();
          }}
          onCapture={handleImageCapture}
          onRetake={resetCapture}
        />
      )}
    </ActionWithInfo>
  );
};

export default FinishDeliveryWithImage;
